export type Permission = string; // e.g., "users:read", "properties:create"
export const ROLE_SK_PREFIX = 'ROLE#';

export const RoleScopeType = {
  ORG: 'ORG',
  PROPERTY: 'PROPERTY',
  PROJECT: 'PROJECT',
  SYSTEM: 'SYSTEM',
} as const;

export type RoleScopeType = (typeof RoleScopeType)[keyof typeof RoleScopeType];

export interface IRole {
  PK: string; // SCOPE#ORG#hv-org-xxx
  SK: string; // ROLE#owner or ROLE#viewer or ROLE#customrole1

  scopeType: RoleScopeType;
  scopeID: string;

  name: string;
  permissions: Permission[];
  isFixed: boolean;

  createdBy: string;
  createdAt: number;
  updatedAt: number;
  typeName: string;
}

export type RoleKey = `${string}|${string}`; // `${PK}|${SK}`
export type RolesScopeKey = `${string}#${string}`; // `${scopeType}#${scopeID}`

export function makeRolePK({ scopeType, scopeID }: { scopeType: RoleScopeType; scopeID: string }) {
  return `SCOPE#${scopeType}#${scopeID}`;
}

export function makeRoleSK({ roleKey }: { roleKey: string }) {
  return `${ROLE_SK_PREFIX}${roleKey}`;
}

export function makeRoleKey(role: Pick<IRole, 'PK' | 'SK'>): RoleKey {
  return `${role.PK}|${role.SK}`;
}

export function makeScopeKey(role: Pick<IRole, 'scopeType' | 'scopeID'>): RolesScopeKey {
  return `${role.scopeType}#${role.scopeID}` as RolesScopeKey;
}

export function makeScopeKeyExplicit(scopeType: IRole['scopeType'], scopeID: string): RolesScopeKey {
  return `${scopeType}#${scopeID}` as RolesScopeKey;
}

export function normalizeRoleKey(roleKey?: string | null): string {
  const value = roleKey?.trim() ?? '';
  if (!value) return '';
  return value.startsWith(ROLE_SK_PREFIX) ? value.slice(ROLE_SK_PREFIX.length) : value;
}

export function isOwnerRoleKey(roleKey?: string | null): boolean {
  return normalizeRoleKey(roleKey) === 'owner';
}
